import { Timestamp } from 'firebase/firestore';

export type ServiceStatus = 'operational' | 'degraded' | 'outage';
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ErrorSource = 'frontend' | 'backend';
export type ErrorStatus = 'open' | 'investigating' | 'resolved';

/**
 * How much this page actually knows right now.
 *
 * Modelled explicitly because the two failure modes look identical to a naive
 * renderer: an empty aggregate and an unreadable aggregate both produce zero
 * errors, and zero errors scores "operational" for every service. `connecting`
 * and `unavailable` exist so neither can ever be painted green.
 */
export type HealthFeedState = 'connecting' | 'live' | 'unavailable';

/** Every visual state a status indicator can take, including "we don't know". */
export type IndicatorState = ServiceStatus | 'unknown';

export interface ErrorDocument {
  id: string;
  timestamp: Timestamp;
  lastSeen: Timestamp;
  count: number;
  source: ErrorSource;
  service: string;
  severity: ErrorSeverity;
  message: string;
  errorCode?: string;
  hash: string;
  resolved: boolean;
  status?: ErrorStatus; // New field for tracking status
  resolvedAt?: Timestamp;
  resolvedBy?: string;
  investigatingAt?: Timestamp;
  investigatingBy?: string;
  stack?: string;
  metadata: {
    environment: string;
    requestId?: string;
    userId?: string;
    url?: string;
    userAgent?: string;
    module?: string;
    component?: string;
    category?: string;
  };
}

/** One recorded failure in the public aggregate: no error content, ever. */
export interface ServiceStatusEntry {
  ts: Timestamp;
  sev: ErrorSeverity;
}

/** `serviceStatus/{service}` — world-readable health signal. */
export interface ServiceStatusDoc {
  service: string;
  recent?: ServiceStatusEntry[];
  updatedAt?: Timestamp;
}

/**
 * What one hour of the fault-history strip says.
 *
 * Deliberately NOT called "uptime". The aggregate records failures only — there
 * is no success or request-volume signal anywhere in it — so an hour with no
 * entries means "nothing was reported", which is weaker than "it was up". The
 * naming, colours and legend all have to keep that distinction visible.
 */
export type HistoryLevel = 'quiet' | 'errors' | 'critical' | 'uncovered';

export interface HistoryBucket {
  /** Start of the hour this bucket covers. */
  start: Date;
  level: HistoryLevel;
  /** Occurrences recorded in the hour. */
  count: number;
  criticalCount: number;
}

export interface ServiceHistory {
  /** Oldest hour first, so the strip reads left to right like a timeline. */
  buckets: HistoryBucket[];
  /**
   * True when the stored aggregate hit its size cap, meaning older hours were
   * discarded and cannot be reported as quiet.
   */
  truncated: boolean;
  /** Oldest moment this history can speak to at all. */
  coverageFrom?: Date;
}

export interface ServiceHealth {
  name: string;
  displayName: string;
  status: ServiceStatus;
  /** Distinct (deduplicated) errors in the window. */
  errorCount: number;
  /** Total times those errors fired — one document can represent hundreds. */
  occurrenceCount: number;
  lastError?: Date;
  /** Hour-by-hour fault history, when it can be derived from the aggregate. */
  history?: ServiceHistory;
}

export interface StatusSummary {
  overall: ServiceStatus;
  services: ServiceHealth[];
  lastUpdated: Date;
}

export const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  chat: 'Chat API',
  pdf: 'PDF Processing',
  llm: 'AI Providers',
  auth: 'Authentication',
  firestore: 'Database',
  audio: 'Audio/Speech',
  'study-guides': 'Study Guides',
  stride: 'Stride Tutoring',
  subscriptions: 'Subscriptions',
  general: 'General',
};

/** What each service covers, so a reader knows whether it affects them. */
export const SERVICE_DESCRIPTIONS: Record<string, string> = {
  chat: 'Tutoring conversations, streaming responses, tool calls',
  pdf: 'Homework uploads, PDF parsing, page extraction',
  llm: 'Claude, Gemini and OpenAI model calls',
  auth: 'Sign-in, sign-up, session refresh',
  firestore: 'Reading and saving your work',
  audio: 'Voice input, transcription, text-to-speech',
  'study-guides': 'Study guide generation',
  stride: 'Assigned homework and remediation',
  subscriptions: 'Billing, plans and credits',
  general: 'Everything not covered above',
};

export const SERVICES = Object.keys(SERVICE_DISPLAY_NAMES);
